import React, { useState } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";

import { AuthContext, ThemeProvider } from "./contexts";
import ChristmasEffects from "./components/ChristmasEffects";
import { setupAxiosInterceptors, resetSessionExpiredFlag } from "./utils";

import Dashboard from "./modules/Dashboard";
import { Login, PasswordForgot } from "./auth";
import { PrivateRoute } from "./routing";
import Sign from "./forms/Sign";
import Tests from "./modules/hiring/Tests";

import "./assets/css/app.css";

const App = () => {
    const [token, setToken] = useState(localStorage.getItem("session_token"));
    const [user, setUser] = useState(null);
    const [dashboardData, setDashboardData] = useState();
    const [userLoaded, setUserLoaded] = useState(false);

    const refreshTimeoutRef = React.useRef(null);

    const fetchDashboardData = React.useCallback(async () => {
        if (!localStorage.getItem("session_token")) return;
        try {
            const response = await axios.get(process.env.REACT_APP_UPA_API_HOST + "dashboard/count-data", {
                headers: { Authorization: "Bearer " + localStorage.getItem("session_token") },
            });
            setDashboardData(response.data);
        } catch (err) {
            setDashboardData(-1);
        }
    }, []);

    const debouncedRefreshDashboard = React.useCallback(() => {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        refreshTimeoutRef.current = setTimeout(() => {
            fetchDashboardData();
        }, 300);
    }, [fetchDashboardData]);

    const contextValue = {
        token: token,
        user: user,
        setToken: setToken,
        setUser: setUser,
        refreshDashboard: debouncedRefreshDashboard,
    };

    React.useEffect(() => {
        setupAxiosInterceptors();
    }, []);

    React.useEffect(() => {
        if (token) {
            resetSessionExpiredFlag();
        }
    }, [token]);

    React.useEffect(() => {
        setUserLoaded(false);
        if (localStorage.getItem("session_token")) {
            axios
                .get(process.env.REACT_APP_UPA_API_HOST + "users/me", {
                    headers: { Authorization: "Bearer " + localStorage.getItem("session_token") },
                })
                .then((response) => {
                    setUser(response.data);
                    setUserLoaded(true);
                    fetchDashboardData();
                })
                .catch((error) => {
                    console.log(error);
                    setUserLoaded(true);
                    setDashboardData(-1);
                });
        } else {
            setUserLoaded(true);
            setDashboardData(-1);
        }
    }, [token]);

    React.useEffect(() => {
        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, []);

    return userLoaded && dashboardData ? (
        <ThemeProvider>
            <AuthContext.Provider value={contextValue}>
                <ChristmasEffects />
                <Routes>
                    <Route
                        path="admin/*"
                        element={
                            <PrivateRoute>
                                <Dashboard countData={dashboardData} />
                            </PrivateRoute>
                        }
                    />
                    <Route path="sign/*" element={<Sign />} />
                    <Route path="tests/*" element={<Tests />} />
                    <Route exact path="login" element={<Login />} />
                    <Route exact path="forgot-password" element={<PasswordForgot />} />
                    <Route path="*" element={<Navigate to="/admin" replace />} />
                </Routes>
            </AuthContext.Provider>
        </ThemeProvider>
    ) : (
        <ThemeProvider>
            <ChristmasEffects />
            <div className="loader-container absolute">
                <CircularProgress size={30} />
            </div>
        </ThemeProvider>
    );
};

export default App;
